import React from 'react';
import { IconButton } from '@material-ui/core';
import { AddOutlined, SyncAlt } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog } from '@klp/design/ConfirmationDialog';
import { MappingTransfer } from './MappingTransfer';
import { MappingModal } from './MappingModal';
import { MappingMenuPopper } from './MappingMenuPopper';
import { useReportMappingActions } from '../hooks/useReportMappingActions';
import { MappingDataLayer } from './MappingForm/MappingDataLayer';
import type { ReportWithMappingDto } from '../types';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';

type MappingActionsProps = {
    reportId: string;
    isMapped: boolean;
    reportName: string | null; // Null is because some reports are corrupted and have no name
    mappingData: ReportWithMappingDto;
    openPopperId: string | null;
    anchorEl: HTMLElement | null;
    onTogglePopper: (
        reportId: string,
    ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
    onClosePopper: () => void;
};

const styles = () => ({
    icon: {
        '& svg': {
            fontSize: '0.875em',
        },
    },
    actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
});

export const MappingActions: React.FC<MappingActionsProps> = ({
    reportId,
    isMapped,
    reportName,
    mappingData,
    openPopperId,
    anchorEl,
    onTogglePopper,
    onClosePopper,
}) => {
    const { t } = useTranslation();
    const {
        deleteRef,
        addRef,
        transferRef,
        showConfirmDialog,
        showAddModal,
        showEditModal,
        showTransferModal,
        isDeleting,
        isTransferring,
        handleDelete,
        handleConfirmDelete,
        handleCancelDelete,
        handleAddMapping,
        handleCloseAddModal,
        handleEditMapping,
        handleCloseEditModal,
        handleTransferMapping,
        handleConfirmTransfer,
        handleCloseTransferModal,
    } = useReportMappingActions({ reportId, isMapped });

    const classes = useKlpStyles({}, styles);

    return (
        <div className={classes.actionsContainer}>
            {isMapped && (
                <>
                    <IconButton
                        aria-label="transfer"
                        size="medium"
                        onClick={handleTransferMapping}
                        ref={transferRef}
                        title={t('reportMapping.mappingTransfer.title')}
                        className={classes.icon}
                    >
                        <SyncAlt />
                    </IconButton>

                    <MappingMenuPopper
                        reportId={reportId}
                        open={openPopperId === reportId}
                        anchorEl={anchorEl}
                        onToggle={onTogglePopper(reportId)}
                        onClose={onClosePopper}
                        onEdit={handleEditMapping}
                        onDelete={handleDelete}
                        menuButtonRef={deleteRef}
                    />

                    <MappingModal
                        open={showEditModal}
                        onClose={handleCloseEditModal}
                        titleKey="reportMapping.mappingEdit.title"
                    >
                        <MappingDataLayer
                            reportId={reportId}
                            preLoadData={mappingData}
                            onClose={handleCloseEditModal}
                        />
                    </MappingModal>

                    <MappingModal
                        open={showTransferModal}
                        onClose={handleCloseTransferModal}
                        titleKey="reportMapping.mappingTransfer.title"
                    >
                        <MappingTransfer
                            reportName={reportName}
                            onTransfer={handleConfirmTransfer}
                            onClose={handleCloseTransferModal}
                            isTransferring={isTransferring}
                        />
                    </MappingModal>

                    <ConfirmationDialog
                        showDialog={showConfirmDialog}
                        anchorRef={deleteRef}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                        dialogContent={t(
                            'reportMapping.mappingDelete.confirmationText',
                        )}
                        confirmText={t('general.deleteYes')}
                        cancelText={t('general.cancel')}
                        isLoading={isDeleting}
                        className="withBlur"
                    />
                </>
            )}
            {!isMapped && (
                <>
                    <IconButton
                        aria-label="add"
                        size="medium"
                        onClick={handleAddMapping}
                        ref={addRef}
                        title={t('reportMapping.mappingAdd.title')}
                        className={classes.icon}
                    >
                        <AddOutlined />
                    </IconButton>

                    <MappingModal
                        open={showAddModal}
                        onClose={handleCloseAddModal}
                        titleKey="reportMapping.mappingAdd.title"
                    >
                        <MappingDataLayer
                            reportId={reportId}
                            preLoadData={mappingData}
                            onClose={handleCloseAddModal}
                        />
                    </MappingModal>
                </>
            )}
        </div>
    );
};
